import React, { useEffect, useRef } from 'react';
import AssetWrapper, { AssetWrapperProps } from 'src/app/atoms/Asset/AssetWrapper';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import {
  PlaceholderContainer,
  SPACING_GAP_VARIABLE,
  TabbedFeaturesAssetCarouselContainer,
} from './TabbedFeaturesAssetCarousel.styles';
import useOnMobile from 'src/hooks/useOnMobile';
import { cssVar } from 'src/app/styles/helper';
import { pauseVideo, playVideo } from 'src/functions/videoHelpers';

interface TabbedFeaturesAssetCarouselProps {
  assets: {
    imageProps?: AssetWrapperProps['imageProps'];
    videoProps?: AssetWrapperProps['videoProps'];
    active?: boolean;
  }[];
  onMoveEnd?: () => void;
}

const SLIDE_SIZE_PERCENTAGE_DESKTOP = 100;
const SLIDE_SIZE_PERCENTAGE_MOBILE = 160;
const TRANSITION_PER_SLIDE_IN_MILLISECONDS = 500;
const MOBILE_PADDING_LEFT_COMPENSATION = 'spacing-xl';

const TabbedFeaturesAssetCarousel = ({ assets, onMoveEnd }: TabbedFeaturesAssetCarouselProps): JSX.Element => {
  const carouselRef = useRef<HTMLDivElement>(null);

  const isMobile = useOnMobile(640);

  const slideSizePercentage = isMobile ? SLIDE_SIZE_PERCENTAGE_MOBILE : SLIDE_SIZE_PERCENTAGE_DESKTOP;

  const placeholderAsset = assets[0];

  const isMoving = useRef(false);
  const previousActiveIndex = useRef<number | null>(null);
  const activeIndex = assets.findIndex((asset) => asset.active);

  const expandedAssets = [...assets, ...assets, ...assets];

  const calculateTransformSteps = (currentIndex: number): number => {
    if (previousActiveIndex.current === null) {
      return 0;
    }

    const totalItems = assets.length;
    const forwardDistance = (currentIndex - previousActiveIndex.current + totalItems) % totalItems;
    const backwardDistance = (previousActiveIndex.current - currentIndex + totalItems) % totalItems;

    if (forwardDistance <= backwardDistance) {
      return forwardDistance;
    }

    // If backward distance is shorter, return negative steps
    return -backwardDistance;
  };

  const getStepDifferenceTransition = (index: number): number => {
    return Math.abs((index - (previousActiveIndex.current ?? 0)) * TRANSITION_PER_SLIDE_IN_MILLISECONDS);
  };

  const applyTransition = (index: number) => {
    if (!carouselRef.current) return;

    const transition = `${getStepDifferenceTransition(index)}ms`;

    carouselRef.current.style.transition = `transform ${transition} ease-in-out`;
  };

  const removeTransition = () => {
    if (!carouselRef.current) return;

    carouselRef.current.style.transition = 'none';
  };

  const getMobilePaddingCompensation = () => {
    return isMobile ? cssVar(MOBILE_PADDING_LEFT_COMPENSATION) : '0px';
  };

  const handleCarouselInitialState = () => {
    if (!carouselRef.current) return;

    applyTransform(activeIndex);

    previousActiveIndex.current = activeIndex;
  };

  const applyTransform = (index: number) => {
    if (!carouselRef.current) return;

    const offset = (index + assets.length) * -slideSizePercentage;
    const gap = `${index + assets.length} * ${cssVar(SPACING_GAP_VARIABLE)}`;
    const mobilePaddingCompensation = getMobilePaddingCompensation();

    carouselRef.current.style.transform = `translateX(calc(${offset}% - ${gap} + ${mobilePaddingCompensation}))`;
  };

  const handleTransitionEnd = (activeIndex: number) => {
    if (!isMoving.current) return;

    removeTransition();
    applyTransform(activeIndex);

    isMoving.current = false;

    onMoveEnd?.();
  };

  const handleCarouselChange = (activeIndex: number) => {
    if (!carouselRef.current) return;

    isMoving.current = true;

    const transformedIndex = previousActiveIndex.current + calculateTransformSteps(activeIndex);

    applyTransition(transformedIndex);
    applyTransform(transformedIndex);

    setTimeout(() => {
      handleTransitionEnd(activeIndex);

      previousActiveIndex.current = activeIndex;
    }, getStepDifferenceTransition(transformedIndex));
  };

  const handleVideos = () => {
    if (!carouselRef.current) return;

    // Pause all videos first
    const allVideos = carouselRef.current.querySelectorAll<HTMLVideoElement>('.tabbed-feature-asset video');
    allVideos.forEach((video) => {
      pauseVideo(video);
    });

    // Play active videos
    const activeVideos = carouselRef.current.querySelectorAll<HTMLVideoElement>('.tabbed-feature-asset.active video');
    activeVideos.forEach((video) => {
      playVideo(video);
    });
  };

  useEffect(() => {
    handleCarouselInitialState();
  }, [isMobile]);

  useEffect(() => {
    if (previousActiveIndex.current === activeIndex || previousActiveIndex.current === null) return;

    handleCarouselChange(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    handleVideos();
  }, [activeIndex]);

  return (
    <Stack position='relative'>
      <PlaceholderContainer>
        <AssetWrapper
          imageProps={placeholderAsset.imageProps}
          videoProps={placeholderAsset.videoProps}
          maxWidth={1280}
          cover
        />
      </PlaceholderContainer>
      <TabbedFeaturesAssetCarouselContainer
        ref={carouselRef}
        $columns={expandedAssets.length}
      >
        {expandedAssets.map((asset, index) => (
          <AssetWrapper
            className={`tabbed-feature-asset ${asset.active ? 'active' : ''}`}
            key={index}
            maxWidth={1280}
            cover
            imageProps={asset.imageProps}
            videoProps={asset.videoProps}
            radius='radius-2xl'
          />
        ))}
      </TabbedFeaturesAssetCarouselContainer>
    </Stack>
  );
};

export default TabbedFeaturesAssetCarousel;
